import { Link } from 'react-router-dom';
import type { AgeRange, EventSummary } from '@mmef/shared/api';
import { describeAgeRange, describePrice } from '@mmef/shared/api';
import { formatLocation, formatTime, formatDistance } from '@mmef/shared/lib';
import { VerificationBadge } from '@mmef/shared/components';
import { SaveEventButton } from '@/components/SaveEventButton';
import { useAppSelector } from '@/app/hooks';
import { selectCurrentUser } from '@mmef/shared/auth';
import { calendarDay } from './homeMetadata';

export interface TimetableRow {
  event: EventSummary;
  distance?: number | null;
}

interface Props {
  rows: TimetableRow[];
  /** Row currently hovered on the map, so the two views highlight as one thing. */
  activeId?: number | null;
  onHover?: (id: number | null) => void;
  /** Picking a row sends the map to that event. Hover only highlights; this one moves it. */
  onSelect?: (id: number) => void;
  selectedId?: number | null;
  /** Distances are only meaningful when the search had somewhere to measure from. */
  showDistance: boolean;
  caption: string;
}

/**
 * The results, one row per event.
 *
 * <p>This was a table of six columns — when, event, ages, price, distance, save. Beside the map it
 * has about 540px, and the columns took their width whether or not they had anything in it: a dash
 * under "Ages" and an empty cell under "Price" still reserved the space, leaving four-word titles
 * wrapping down five lines. Rows give the width to whatever the event actually has, so a missing
 * age band costs nothing rather than costing a column.
 *
 * <p>Each row reads in the order the decision is made: what it is, when and where, who it's for,
 * who runs it — with the date on the left to scan down and the price on the right.
 *
 * <p>Rows report hover so the map can light the matching pin. That pairing is the reason both are
 * on screen at once rather than behind tabs.
 */
export function EventTimetable({
  rows,
  activeId,
  onHover,
  onSelect,
  selectedId,
  showDistance,
  caption,
}: Props) {
  // SaveEventButton renders nothing for a signed-out visitor.
  const showSave = Boolean(useAppSelector(selectCurrentUser));

  return (
    <ol className="results-list" aria-label={caption}>
      {rows.map(({ event, distance }) => {
        const { month, day } = calendarDay(event.startTime, event.timezone);
        const price = describePrice(event.price);
        const ages = describeAges(event.ageRanges, event.title);
        const place = formatLocation([event.venueName, event.city]);

        return (
          <li
            key={event.id}
            className={
              [
                'result',
                activeId === event.id ? 'is-active' : '',
                selectedId === event.id ? 'is-selected' : '',
                onSelect ? 'is-pickable' : '',
              ]
                .filter(Boolean)
                .join(' ')
            }
            onMouseEnter={() => onHover?.(event.id)}
            onMouseLeave={() => onHover?.(null)}
            /*
             * Picking a row shows it on the map. The guard matters: the row already contains a link
             * to the event and a save button, and swallowing those clicks would break the two
             * things people actually came to press.
             */
            onClick={(e) => {
              if ((e.target as HTMLElement).closest('a, button, input')) return;
              onSelect?.(event.id);
            }}
          >
            <time className="result__date" dateTime={event.startTime}>
              <span className="result__month">{month}</span>
              <span className="result__day">{day}</span>
              <span className="result__weekday">{weekday(event.startTime, event.timezone)}</span>
            </time>

            <div className="result__body">
              <h2 className="result__title">
                <Link to={`/events/${event.id}`}>{event.title}</Link>
              </h2>

              {/* Both ends of the event: these run from two hours to ten, which is most of what
                  separates a weeknight meetup from a Saturday. */}
              <p className="result__where">
                {formatTime(event.startTime, event.timezone)}
                {event.endTime && ` – ${formatTime(event.endTime, event.timezone)}`}
                {place && ` · ${place}`}
              </p>

              {ages && <p className="result__ages">{ages}</p>}

              {event.organiser && (
                <p className="result__who">
                  {event.organiser}{' '}
                  {/* Only the positive one. "Unconfirmed listing" was on nearly every row, where a
                      warning repeated that often reads as decoration; it belongs on the event page,
                      which has room to say what it means. */}
                  {event.organiserVerification === 'PLATFORM_LINKED' && (
                    <VerificationBadge tier={event.organiserVerification} />
                  )}
                </p>
              )}
            </div>

            <div className="result__aside">
              {price && <span className="result__price">{price}</span>}
              {showDistance && (
                <span className="result__distance">
                  {typeof distance === 'number' ? formatDistance(distance) : 'no location'}
                </span>
              )}
              {showSave && <SaveEventButton eventId={event.id} />}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** The weekday in the event's own zone — "Sat". */
function weekday(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone }).format(new Date(iso));
  } catch {
    return new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'UTC' }).format(new Date(iso));
  }
}

const GROUP_WORDS = ['', '', 'two', 'three', 'four', 'five', 'six'];

/**
 * The age line, or nothing at all.
 *
 * <p>Nothing when the organiser stated no ages — 8 of the 27 events listed when this was written —
 * and nothing when the title already carries the same numbers, which several do ("…Leeds, ages 21
 * to 34"). Printing it twice in two lines of the same row is what made the first attempt at this
 * look cluttered.
 *
 * <p>Several bands become a span and a count rather than a list: "Ages 21–45, in three groups" is
 * one fact to read where "21–30, 27–37, 34–45" is six numbers to hold. The bands themselves are on
 * the event page, where somebody is deciding rather than scanning.
 */
export function describeAges(ranges: AgeRange[], title: string): string | null {
  if (ranges.length === 0) return null;

  if (ranges.length === 1) {
    const band = describeAgeRange(ranges[0]);
    return repeatsTitle(ranges[0], title) ? null : `Ages ${band}`;
  }

  const lowest = Math.min(...ranges.map((r) => r.minAge ?? Infinity));
  const open = ranges.some((r) => r.maxAge === null);
  const highest = Math.max(...ranges.map((r) => r.maxAge ?? 0));
  const groups = GROUP_WORDS[ranges.length] ?? `${ranges.length}`;
  const span = open || !Number.isFinite(lowest) ? `${lowest} and over` : `${lowest}–${highest}`;
  return `Ages ${span}, in ${groups} groups`;
}

/**
 * Whether the title already states this band, so the line beneath would only repeat it.
 *
 * <p>Bounded by digits rather than word boundaries: "Over 30s Muslim Marriage Event" states the
 * same 30 as the band does, and a word boundary doesn't see it because a letter follows the number.
 */
function repeatsTitle({ minAge, maxAge }: AgeRange, title: string): boolean {
  const numbers = [minAge, maxAge].filter((n): n is number => n !== null);
  return numbers.length > 0 && numbers.every((n) => new RegExp(`(?<!\\d)${n}(?!\\d)`).test(title));
}
